import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, LoadingSpinner, Button } from '@components/common';
import { Collection } from '@components/common/Collection';
import { BaseModal } from '@components/modals';
import { ImageManager } from '@components/attachments';
import { useLanguage } from '@contexts/LanguageContext';
import { useNotification } from '@hooks/useNotification';
import { useCategoryPermissions } from '@hooks/useCategoryPermissions';
import Icon from '@components/Icon/Icon';
import api from '@services/api';
import './CategoryViewPage.scss';

const CategoryViewPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const notify = useNotification();
    const [category, setCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalState, setModalState] = useState({ isOpen: false, mode: 'delete' });

    // Permissions pour cette catégorie
    const { permissions: categoryPermissions, loading: permissionsLoading } = useCategoryPermissions(id);

    useEffect(() => {
        const fetchCategory = async () => {
            try {
                setLoading(true);
                const response = await api.get(`/categories/${id}`);
                setCategory(response.data);
                setError(null);
            } catch (err) {
                console.error('Failed to fetch category:', err);
                setError(t('categoryView.fetchError'));
                notify.error(t('categoryView.fetchError'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchCategory();
        }
    }, [id, t]); // Removed notify from dependencies to prevent infinite re-renders

    const handleBack = () => {
        navigate('/categories');
    };

    const handleEdit = () => {
        navigate(`/categories/${id}/edit`);
    };

    const handleDelete = () => {
        setModalState({ isOpen: true, mode: 'delete' });
    };

    const handleModalClose = () => {
        setModalState({ isOpen: false, mode: 'delete' });
    };

    const handleModalSuccess = () => {
        if (modalState.mode === 'delete') {
            navigate('/categories');
        }
    };

    if (loading) {
        return (
            <div className="category-view-page">
                <div className="loading-container">
                    <LoadingSpinner />
                    <p>{t('categoryView.loading')}</p>
                </div>
            </div>
        );
    }

    if (error || !category) {
        return (
            <div className="category-view-page">
                <Card className="error-card">
                    <div className="error-content">
                        <h2>{t('categoryView.errorTitle')}</h2>
                        <p>{error || t('categoryView.notFound')}</p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="category-view-page">
            <div className="category-details">

                <Card className="category-info-card">
                    <div className="category-timestamps">
                        <div className="timestamp">
                            <span className="label">{t('categories.categoryView.createdAt')}</span>
                            <span className="value">{new Date(category.created_at).toLocaleDateString()}</span>
                        </div>
                        {category.updated_at && (
                            <div className="timestamp">
                                <span className="label">{t('categories.categoryView.updatedAt')}</span>
                                <span className="value">{new Date(category.updated_at).toLocaleDateString()}</span>
                            </div>
                        )}
                    </div>

                    <div className="category-layout">
                        <div className="category-image-section">
                            <ImageManager
                                entity={category}
                                editable={false}
                                showUploader={false}
                                maxImages={1}
                                className="gallery-view"
                                maxFileSize={5 * 1024 * 1024} // 5MB for category images
                            />
                        </div>

                        <div className="category-details-section">
                            <div className="category-header">
                                {category.icon && (
                                    <div className="category-icon">
                                        <Icon icon={category.icon} size="lg" />
                                    </div>
                                )}
                                <div className="category-info">
                                    <h1>{category.name}</h1>
                                    {category.description && (
                                        <p className="category-description">{category.description}</p>
                                    )}
                                </div>
                            </div>

                            {/* Edit and Delete buttons */}
                            <div className="category-actions">
                                {!permissionsLoading && categoryPermissions.canUpdate && (
                                    <Button
                                        variant="primary"
                                        onClick={handleEdit}
                                    >
                                        <span>{t('common.edit')}</span>
                                    </Button>
                                )}

                                {!permissionsLoading && categoryPermissions.canDelete && (
                                    <Button
                                        variant="danger"
                                        onClick={handleDelete}
                                    >
                                        <span>{t('common.delete')}</span>
                                    </Button>
                                )}
                            </div>
                        </div>

                    </div>
                </Card>

            </div>
            <div className="category-resources">
                <Collection
                    modelName="resources"
                    displayType="grid"
                    title={t('categoryView.resourcesInCategory')}
                    description={t('categoryView.resourcesDescription', { categoryName: category.name })}
                    queryParams={{ category_id: category.id }}
                    enableSearch={true}
                    enablePagination={true}
                    className="category-resources-collection"
                />
            </div>

            <BaseModal
                isOpen={modalState.isOpen}
                onClose={handleModalClose}
                onSuccess={handleModalSuccess}
                mode={modalState.mode}
                item={category}
                itemType="category"
                deleteFn={async (id) => await api.delete(`/categories/${id}`)}
                size="small"
            />
        </div>
    );
};

export default CategoryViewPage;
